import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  ContentSection,
  Layout,
  PageWrap,
  Paragraph,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { Analytics } from '../../services/analytics';
import { creditCheckPageEvent } from '../../services/analytics/analyticsEvents';
import {
  creditApplicationSelectors,
  setCanRedirectToOnfido,
} from '../../services/redux/features/creditApplication.slice';
import {
  selectCreditApplicationIdFromDataStorage,
  selectQuoteIdFromDataStorage,
} from '../../services/redux/features/dataStorage.redux';
import { loadingActions, loadingSelectors } from '../../services/redux/features/loading.slice';
import { useNavigate } from '../../services/routing/navigate';
import { isIdCardStipulationStatusDefined, shouldCheckIdStipulationStatus } from '../FormPage/utils/checkIDStatusUtil';

export const CreditCheckPendingPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const creditApplicationId = useSelector(selectCreditApplicationIdFromDataStorage);
  const creditApplicationDetails = useSelector(creditApplicationSelectors.selectCreditApplicationDetails);
  const quoteId = useSelector(selectQuoteIdFromDataStorage);
  const canRedirectToOnfido = useSelector(creditApplicationSelectors.selectCanRedirectToOnfido);

  const navigate = useNavigate();

  const isLoading = useSelector(loadingSelectors.selectLoading);

  useEffect(() => {
    dispatch(loadingActions.setLoading(true));
    Analytics.push(creditCheckPageEvent('pending'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const solvencyCheckStatus = creditApplicationDetails.creditDecision?.solvencyCheck?.status;

  useEffect(() => {
    if (solvencyCheckStatus && shouldCheckIdStipulationStatus(solvencyCheckStatus)) {
      const executeIdCheck = async () => {
        if (!creditApplicationId || !quoteId) return;

        const idCardStipulationStatusDefined = await isIdCardStipulationStatusDefined(creditApplicationId, quoteId);

        dispatch(setCanRedirectToOnfido(idCardStipulationStatusDefined));
        dispatch(loadingActions.setLoading(false));
      };

      executeIdCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solvencyCheckStatus, creditApplicationId, quoteId]);

  const handleRedirection = useCallback(() => {
    if (canRedirectToOnfido) {
      navigate.identLanding();
    } else {
      navigate.form('/5');
    }
  }, [navigate, canRedirectToOnfido]);

  if (isLoading) return null;

  return (
    <PageWrap>
      <ContentSection className="u-border">
        <SectionHeading center level={2}>
          {canRedirectToOnfido ? t('creditCheckPending:heading') : t('creditCheckPending:headingError')}
        </SectionHeading>
        <br />
        <Layout center>
          <div className="u-block-center u-h1">
            <i className="c-icon  c-icon--[semantic-reload] c-icon--xxlarge" />
          </div>
        </Layout>
        <Layout center>
          <Layout.Item default="3/4">
            <Paragraph className="u-text-center">
              {canRedirectToOnfido ? t('creditCheckPending:text') : t('creditCheckPending:errorMessage')}
            </Paragraph>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <ContentSection>
        <Layout center>
          <Layout.Item default="1/6">
            <ButtonContainer center>
              <Button onClick={handleRedirection}>{t('creditCheck:continueButton')}</Button>
            </ButtonContainer>
          </Layout.Item>
        </Layout>
      </ContentSection>
      <br />
    </PageWrap>
  );
};
